<template>
  <div class="op-emergency-page" dir="rtl">
    <!-- ==================== PAGE HEADER ==================== -->
    <div class="op-header-row mb-5">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-3 mb-1 flex-wrap">
          <h1 class="op-main-title d-flex align-center">
            <div class="op-icon-header-badge me-2">
              <v-icon size="26" color="#DC2626">mdi-alert-octagon</v-icon>
            </div>
            <span>طلبات إلغاء الطوارئ</span>
          </h1>
          <span v-if="pendingCount > 0" class="op-pill-badge bg-amber-soft text-amber">
            {{ pendingCount }} طلب قيد المراجعة
          </span>
          <span v-else class="op-pill-badge bg-emerald-soft text-emerald">
            لا توجد طلبات معلقة
          </span>
        </div>
        <p class="op-sub-title mb-0">
          مراجعة طلبات الإلغاء الطارئ للرحلات المسؤولة عنها واتخاذ قرارات الاعتماد أو الرفض
        </p>
      </div>

      <div class="op-header-actions d-flex align-center gap-2 flex-wrap">
        <v-btn
          depressed
          class="op-btn-refresh"
          @click="loadRequests"
          :loading="isLoading"
        >
          <v-icon small class="me-1">mdi-refresh</v-icon>
          تحديث
        </v-btn>
      </div>
    </div>

    <!-- ==================== TOP SUMMARY / KPI CARDS ==================== -->
    <div class="op-kpi-grid mb-5">
      <!-- 1. Total Requests -->
      <div
        class="op-kpi-card"
        :class="{ 'op-kpi-card-active': statusFilter === 'all' }"
        @click="setStatusFilter('all')"
      >
        <div class="kpi-content-side">
          <div class="kpi-label">إجمالي الطلبات</div>
          <div class="kpi-value text-navy">{{ emergencyRequests.length }}</div>
        </div>
        <div class="kpi-icon-side bg-indigo-50 text-navy">
          <v-icon size="22" color="#082C69">mdi-format-list-bulleted</v-icon>
        </div>
      </div>

      <!-- 2. Pending Requests -->
      <div
        class="op-kpi-card"
        :class="{ 'op-kpi-card-active': statusFilter === 'pending' }"
        @click="setStatusFilter('pending')"
      >
        <div class="kpi-content-side">
          <div class="kpi-label">قيد المراجعة</div>
          <div class="kpi-value text-amber">{{ pendingCount }}</div>
        </div>
        <div class="kpi-icon-side bg-amber-50 text-amber">
          <v-icon size="22" color="#D97706">mdi-clock-outline</v-icon>
        </div>
      </div>

      <!-- 3. Approved Requests -->
      <div
        class="op-kpi-card"
        :class="{ 'op-kpi-card-active': statusFilter === 'approved' }"
        @click="setStatusFilter('approved')"
      >
        <div class="kpi-content-side">
          <div class="kpi-label">تمت الموافقة</div>
          <div class="kpi-value text-emerald">{{ approvedCount }}</div>
        </div>
        <div class="kpi-icon-side bg-emerald-50 text-emerald">
          <v-icon size="22" color="#16A34A">mdi-check-circle-outline</v-icon>
        </div>
      </div>

      <!-- 4. Rejected Requests -->
      <div
        class="op-kpi-card"
        :class="{ 'op-kpi-card-active': statusFilter === 'rejected' }"
        @click="setStatusFilter('rejected')"
      >
        <div class="kpi-content-side">
          <div class="kpi-label">مرفوضة</div>
          <div class="kpi-value text-rose">{{ rejectedCount }}</div>
        </div>
        <div class="kpi-icon-side bg-rose-50 text-rose">
          <v-icon size="22" color="#DC2626">mdi-close-circle-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== SEARCH & FILTER TOOLBAR ==================== -->
    <div class="op-filter-card mb-5">
      <div class="d-flex align-center justify-space-between flex-wrap gap-3">
        <div class="d-flex align-center flex-grow-1 flex-wrap gap-3">
          <!-- Search input -->
          <div class="op-search-box flex-grow-1">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="ابحث بكود الطلب، الرحلة، السائق أو السبب..."
              dense
              outlined
              hide-details
              clearable
              class="op-search-input"
              @input="onSearchInput"
              @click:clear="clearSearch"
            ></v-text-field>
          </div>

          <!-- Status Filter -->
          <div class="op-select-box">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="تصفية حسب الحالة"
              dense
              outlined
              hide-details
              class="op-filter-select"
              @change="loadRequests"
            ></v-select>
          </div>
        </div>

        <!-- Filter Reset -->
        <div v-if="search || statusFilter !== 'pending'">
          <v-btn text small color="#64748B" class="font-weight-medium" @click="resetFilters">
            <v-icon x-small class="me-1">mdi-filter-off</v-icon>
            إعادة تعيين التصفية
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ==================== DATA TABLE CARD ==================== -->
    <div class="op-table-card">
      <v-data-table
        :headers="tableHeaders"
        :items="emergencyRequests"
        :loading="isLoading"
        :loading-text="'جاري تحميل طلبات الطوارئ...'"
        :no-data-text="''"
        item-key="id"
        class="op-data-table elevation-0"
        :footer-props="{
          'items-per-page-text': 'عدد الصفوف في الصفحة:',
          'items-per-page-options': [10, 25, 50, 100],
          'items-per-page-all-text': 'الكل'
        }"
      >
        <!-- Custom empty state slot -->
        <template v-slot:no-data>
          <div class="op-empty-state py-10 px-4 text-center">
            <div class="op-empty-icon-wrap mb-3 mx-auto">
              <v-icon size="36" color="#94A3B8">mdi-shield-check-outline</v-icon>
            </div>
            <h4 class="font-weight-bold text-navy mb-1">
              لا توجد طلبات إلغاء طارئ قيد المراجعة حاليًا
            </h4>
            <p class="caption text-muted mb-4" style="max-width: 420px; margin: 0 auto;">
              جميع الرحلات تعمل وفق الجداول التشغيلية ولا توجد طلبات إيقاف طارئة تحتاج إلى تدخل.
            </p>
            <v-btn v-if="statusFilter !== 'all' || search" small depressed color="#082C69" dark @click="resetFilters">
              عرض كافة الطلبات
            </v-btn>
          </div>
        </template>

        <!-- Request ID -->
        <template v-slot:item.id="{ item }">
          <span class="op-code-pill font-weight-bold">#{{ item.id }}</span>
        </template>

        <!-- Planned Trip Code -->
        <template v-slot:item.planned_trip="{ item }">
          <div class="d-flex flex-column">
            <span class="op-trip-pill font-weight-bold">
              {{ formatTripCode(item.planned_trip_id) }}
            </span>
            <small class="text-muted mt-1" v-if="item.planned_trip && item.planned_trip.planned_date">
              <v-icon x-small color="#64748B" class="me-1">mdi-calendar</v-icon>
              {{ item.planned_trip.planned_date }}
            </small>
          </div>
        </template>

        <!-- Driver / Captain -->
        <template v-slot:item.driver="{ item }">
          <div class="d-flex align-center py-2" v-if="item.driver">
            <v-avatar size="32" class="me-2 elevation-1" color="#F1F5F9">
              <v-img :src="getAvatar(item.driver.avatar)"></v-img>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-navy caption">{{ item.driver.name }}</div>
              <div class="text-xxs text-muted font-monospace" dir="ltr" style="text-align: right;">
                {{ item.driver.tel_number || item.driver.email }}
              </div>
            </div>
          </div>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Route -->
        <template v-slot:item.route="{ item }">
          <div v-if="item.planned_trip && item.planned_trip.route" class="d-flex flex-column">
            <span class="font-weight-bold text-navy caption">
              {{ item.planned_trip.route.name }}
            </span>
            <small class="text-muted font-monospace" v-if="item.planned_trip.route.route_code">
              {{ item.planned_trip.route.route_code }}
            </small>
          </div>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Reason -->
        <template v-slot:item.reason="{ item }">
          <div class="d-flex flex-column align-start">
            <span class="op-reason-badge">
              <v-icon x-small color="#D97706" class="me-1">mdi-alert-circle</v-icon>
              {{ getReasonLabel(item) }}
            </span>
            <span v-if="item.note" class="caption text-muted mt-1 text-truncate" style="max-width: 180px;" :title="item.note">
              {{ item.note }}
            </span>
          </div>
        </template>

        <!-- Requested At -->
        <template v-slot:item.requested_at="{ item }">
          <div class="d-flex flex-column">
            <span class="caption text-navy font-weight-medium">
              {{ formatDateTime(item.requested_at || item.created_at) }}
            </span>
            <small class="text-muted" v-if="formatTimeAgo(item.requested_at || item.created_at)">
              {{ formatTimeAgo(item.requested_at || item.created_at) }}
            </small>
          </div>
        </template>

        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <span :class="getStatusBadgeClass(item.status)">
            <span class="status-dot me-1"></span>
            {{ getStatusLabel(item.status) }}
          </span>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center gap-1">
            <!-- View Details -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#082C69"
                  v-bind="attrs"
                  v-on="on"
                  class="op-action-btn"
                  @click="viewDetails(item)"
                >
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <span>عرض تفاصيل الطلب</span>
            </v-tooltip>

            <!-- Approve Button (Only if Pending) -->
            <v-tooltip v-if="item.status === 'pending'" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#16A34A"
                  v-bind="attrs"
                  v-on="on"
                  class="op-action-btn op-action-approve"
                  @click="openActionDialog(item, 'approve')"
                >
                  <v-icon small>mdi-check-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>اعتماد طلب الإلغاء</span>
            </v-tooltip>

            <!-- Reject Button (Only if Pending) -->
            <v-tooltip v-if="item.status === 'pending'" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#DC2626"
                  v-bind="attrs"
                  v-on="on"
                  class="op-action-btn op-action-reject"
                  @click="openActionDialog(item, 'reject')"
                >
                  <v-icon small>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>رفض طلب الإلغاء</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- ==================== DETAILS MODAL ==================== -->
    <v-dialog v-model="detailsDialog" max-width="750px" scrollable>
      <v-card v-if="selectedRequest" class="op-dialog-card" dir="rtl">
        <!-- Dialog Header -->
        <v-card-title class="op-dialog-header d-flex align-center justify-space-between py-4 px-6">
          <div class="d-flex align-center gap-2 flex-wrap">
            <div class="op-icon-header-box me-2" style="background-color: #FEE2E2;">
              <v-icon size="20" color="#DC2626">mdi-alert-octagon</v-icon>
            </div>
            <div>
              <div class="d-flex align-center gap-2">
                <span class="caption text-muted">تفاصيل طلب إنهاء الطوارئ</span>
                <span class="op-code-pill font-weight-bold">#{{ selectedRequest.id }}</span>
              </div>
              <h3 class="op-dialog-title mb-0 mt-1">
                رحلة {{ formatTripCode(selectedRequest.planned_trip_id) }}
                <span v-if="selectedRequest.planned_trip && selectedRequest.planned_trip.route">
                  — {{ selectedRequest.planned_trip.route.name }}
                </span>
              </h3>
            </div>
          </div>
          <v-btn icon small @click="detailsDialog = false">
            <v-icon color="#64748B">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- 4 Stat Cards Grid -->
          <div class="op-trip-modal-grid mb-5">
            <div class="op-modal-stat-card">
              <span class="stat-label">كود الطلب</span>
              <span class="stat-value text-navy font-weight-bold">#{{ selectedRequest.id }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">الرحلة المجدولة</span>
              <span class="stat-value text-navy font-weight-bold">{{ formatTripCode(selectedRequest.planned_trip_id) }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">تاريخ ووقت الطلب</span>
              <span class="stat-value text-navy">{{ formatDateTime(selectedRequest.requested_at || selectedRequest.created_at) }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">حالة الطلب</span>
              <span class="stat-value">
                <span :class="getStatusBadgeClass(selectedRequest.status)">
                  {{ getStatusLabel(selectedRequest.status) }}
                </span>
              </span>
            </div>
          </div>

          <!-- Driver & Vehicle Row -->
          <div class="row mb-3">
            <!-- Driver Info Card -->
            <div class="col-12 col-md-6 py-1">
              <div class="op-info-card pa-3 rounded-lg border">
                <div class="d-flex align-center mb-2">
                  <v-avatar size="36" class="me-2" color="#F1F5F9">
                    <v-img :src="getAvatar(selectedRequest.driver ? selectedRequest.driver.avatar : null)"></v-img>
                  </v-avatar>
                  <div>
                    <span class="caption text-muted d-block">السائق / الكابتن</span>
                    <span class="font-weight-bold text-navy">{{ selectedRequest.driver ? selectedRequest.driver.name : '—' }}</span>
                  </div>
                </div>
                <div class="caption text-muted font-monospace" dir="ltr" style="text-align: right;">
                  {{ selectedRequest.driver ? (selectedRequest.driver.tel_number || selectedRequest.driver.email) : '—' }}
                </div>
              </div>
            </div>

            <!-- Vehicle Info Card -->
            <div class="col-12 col-md-6 py-1">
              <div class="op-info-card pa-3 rounded-lg border">
                <div class="d-flex align-center mb-2">
                  <div class="op-mini-icon-box me-2" style="background-color: #EEF2FF; padding: 6px; border-radius: 6px;">
                    <v-icon size="20" color="#082C69">mdi-bus</v-icon>
                  </div>
                  <div>
                    <span class="caption text-muted d-block">وسيلة النقل المسندة</span>
                    <span class="font-weight-bold text-navy">
                      {{ (selectedRequest.planned_trip && selectedRequest.planned_trip.bus) ? (selectedRequest.planned_trip.bus.license || selectedRequest.planned_trip.bus.name || 'مركبة مسندة') : 'غير محددة' }}
                    </span>
                  </div>
                </div>
                <div class="caption text-muted">
                  {{ (selectedRequest.planned_trip && selectedRequest.planned_trip.route) ? selectedRequest.planned_trip.route.name : '—' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Emergency Reason & Note Section -->
          <div class="op-reason-detail-card mb-4 pa-4 rounded-lg" style="background-color: #FFFBEB; border: 1px solid #FDE68A;">
            <div class="d-flex align-center mb-2">
              <v-icon size="20" color="#D97706" class="me-2">mdi-alert-circle</v-icon>
              <span class="font-weight-bold" style="color: #92400E;">سبب طلب إنهاء الطوارئ:</span>
              <span class="op-pill-count ms-2 font-weight-bold" style="background-color: #FEF3C7; color: #B45309; padding: 2px 8px; border-radius: 6px;">
                {{ getReasonLabel(selectedRequest) }}
              </span>
            </div>
            <div class="caption mt-2" style="color: #78350F; line-height: 1.6;">
              <strong>ملاحظات الكابتن:</strong>
              {{ selectedRequest.note || 'لا توجد ملاحظات تفصيلية مضافة من الكابتن.' }}
            </div>
          </div>

          <!-- Review Details if already reviewed -->
          <div v-if="selectedRequest.status !== 'pending'" class="op-review-card pa-4 rounded-lg border" style="background-color: #F8FAFC;">
            <h4 class="caption font-weight-bold text-navy mb-2 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-clipboard-check-outline</v-icon>
              تفاصيل مراجعة الإدارة:
            </h4>
            <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-2 caption text-muted">
              <div><strong>المراجع:</strong> {{ selectedRequest.reviewer ? selectedRequest.reviewer.name : 'مسؤول العمليات' }}</div>
              <div><strong>تاريخ المراجعة:</strong> {{ formatDateTime(selectedRequest.reviewed_at) }}</div>
            </div>
            <div v-if="selectedRequest.review_note" class="caption pa-2 rounded" style="background-color: #FFFFFF; border: 1px solid #E2E8F0;">
              <strong>ملاحظات / سبب الإجراء:</strong> {{ selectedRequest.review_note }}
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 bg-slate-50 justify-space-between">
          <div>
            <template v-if="selectedRequest.status === 'pending'">
              <v-btn
                depressed
                color="#16A34A"
                dark
                class="font-weight-bold me-2"
                @click="detailsDialog = false; openActionDialog(selectedRequest, 'approve')"
              >
                <v-icon small class="me-1">mdi-check-circle</v-icon>
                موافقة على الإلغاء
              </v-btn>

              <v-btn
                outlined
                color="#DC2626"
                class="font-weight-bold"
                @click="detailsDialog = false; openActionDialog(selectedRequest, 'reject')"
              >
                <v-icon small class="me-1">mdi-close-circle</v-icon>
                رفض الطلب
              </v-btn>
            </template>
          </div>

          <v-btn depressed class="op-btn-secondary" @click="detailsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== ACTION CONFIRMATION DIALOG (APPROVE / REJECT) ==================== -->
    <v-dialog v-model="actionDialog" max-width="520px" persistent>
      <v-card v-if="selectedRequest" class="op-dialog-card" dir="rtl">
        <v-card-title class="op-dialog-header d-flex align-center py-4 px-6">
          <div class="op-icon-header-box me-2" :style="{ backgroundColor: actionType === 'approve' ? '#DCFCE7' : '#FEE2E2' }">
            <v-icon size="20" :color="actionType === 'approve' ? '#16A34A' : '#DC2626'">
              {{ actionType === 'approve' ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
          </div>
          <span class="font-weight-bold text-navy">
            {{ actionType === 'approve' ? 'اعتماد طلب إلغاء الطوارئ' : 'رفض طلب إلغاء الطوارئ' }}
            #{{ selectedRequest.id }}
          </span>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <div
            class="pa-3 rounded-lg mb-4 caption font-weight-medium"
            :style="{
              backgroundColor: actionType === 'approve' ? '#F0FDF4' : '#FEF2F2',
              border: '1px solid ' + (actionType === 'approve' ? '#86EFAC' : '#FECACA'),
              color: actionType === 'approve' ? '#166534' : '#991B1B'
            }"
          >
            {{ actionType === 'approve'
              ? 'سيتم اعتماد طلب إنهاء الطوارئ المقدم من الكابتن وإنهاء الرحلة وتوثيق سبب الإلغاء الطارئ.'
              : 'سيتم رفض طلب إنهاء الطوارئ المقدم من الكابتن. إدخال سبب الرفض إلزامي للتوثيق وإشعار الكابتن.'
            }}
          </div>

          <div class="bg-slate-50 pa-3 rounded-lg border font-weight-bold text-navy caption mb-4">
            الرحلة: {{ formatTripCode(selectedRequest.planned_trip_id) }} — كابتن: {{ selectedRequest.driver ? selectedRequest.driver.name : '—' }}
          </div>

          <div>
            <label class="caption font-weight-bold text-navy d-block mb-1">
              {{ actionType === 'reject' ? 'سبب الرفض (إلزامي): *' : 'ملاحظات المراجعة (اختياري):' }}
            </label>
            <v-textarea
              v-model="reviewNote"
              :placeholder="actionType === 'reject' ? 'اكتب سبب الرفض بالتفصيل (3 أحرف على الأقل)...' : 'أضف أي ملاحظات إدارية هنا...'"
              outlined
              dense
              rows="3"
              hide-details
              class="op-textarea-field"
            ></v-textarea>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4 justify-end gap-2 bg-slate-50">
          <v-btn text :disabled="isSubmitting" @click="actionDialog = false">
            إلغاء
          </v-btn>

          <v-btn
            depressed
            :loading="isSubmitting"
            :disabled="isSubmitting"
            :color="actionType === 'approve' ? '#16A34A' : '#DC2626'"
            class="white--text font-weight-bold px-5"
            @click="submitAction"
          >
            {{ actionType === 'approve' ? 'تأكيد الاعتماد' : 'تأكيد الرفض' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import auth from "@/services/AuthService";
import axios from "axios";

export default {
  name: "DriverEmergencyRequests",
  data() {
    return {
      emergencyRequests: [],
      isLoading: false,
      search: "",
      statusFilter: "pending",
      detailsDialog: false,
      actionDialog: false,
      selectedRequest: null,
      actionType: "approve", // 'approve' or 'reject'
      reviewNote: "",
      isSubmitting: false,
      statusOptions: [
        { text: "قيد المراجعة", value: "pending" },
        { text: "تمت الموافقة", value: "approved" },
        { text: "مرفوضة", value: "rejected" },
        { text: "الكل", value: "all" },
      ],
      reasonsMap: {
        vehicle_breakdown: "عطل في المركبة",
        accident: "حادث",
        medical_emergency: "حالة صحية طارئة",
        road_block: "تعطل الطريق",
        passenger_issue: "مشكلة مع راكب",
        other: "أخرى",
      },
      tableHeaders: [
        { text: "كود الطلب", value: "id", width: "90px", align: "center", sortable: true },
        { text: "الرحلة", value: "planned_trip", width: "130px", sortable: false },
        { text: "السائق / الكابتن", value: "driver", width: "210px", sortable: false },
        { text: "المسار", value: "route", width: "200px", sortable: false },
        { text: "سبب الطوارئ", value: "reason", width: "190px", sortable: false },
        { text: "تاريخ الطلب", value: "requested_at", width: "170px", sortable: true },
        { text: "الحالة", value: "status", width: "130px", align: "center", sortable: true },
        { text: "الإجراءات", value: "actions", width: "130px", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    pendingCount() {
      return this.emergencyRequests.filter((r) => r.status === "pending").length;
    },
    approvedCount() {
      return this.emergencyRequests.filter((r) => r.status === "approved").length;
    },
    rejectedCount() {
      return this.emergencyRequests.filter((r) => r.status === "rejected").length;
    },
  },
  mounted() {
    this.loadRequests();
  },
  methods: {
    loadRequests() {
      this.isLoading = true;
      axios
        .get("/driver-emergency-requests", {
          params: {
            status: this.statusFilter,
            search: this.search,
          },
        })
        .then((response) => {
          this.emergencyRequests = response.data || [];
        })
        .catch((error) => {
          this.$notify({
            title: "خطأ",
            text: "حدث خطأ أثناء جلب طلبات إنهاء الطوارئ",
            type: "error",
          });
          console.error(error);
          if (error.response && error.response.data && error.response.data.message) {
            auth.checkError(error.response.data.message, this.$router, this.$swal);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onSearchInput() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.loadRequests();
      }, 350);
    },

    clearSearch() {
      this.search = "";
      this.loadRequests();
    },

    setStatusFilter(val) {
      this.statusFilter = val;
      this.loadRequests();
    },

    resetFilters() {
      this.search = "";
      this.statusFilter = "pending";
      this.loadRequests();
    },

    formatTripCode(id) {
      if (!id) return "—";
      return "P-" + String(id).padStart(4, "0");
    },

    formatDateTime(val) {
      if (!val) return "—";
      return this.$moment ? this.$moment(val).format("YYYY/MM/DD - hh:mm A") : val;
    },

    formatTimeAgo(val) {
      if (!val) return "";
      return this.$moment ? this.$moment(val).fromNow() : "";
    },

    getAvatar(avatar) {
      if (!avatar) return require("@/assets/images/avatars/1.png");
      if (avatar.startsWith("http")) return avatar;
      return avatar;
    },

    getReasonLabel(item) {
      if (!item) return "—";
      if (item.reason_label) return item.reason_label;
      return this.reasonsMap[item.reason] || item.reason || "—";
    },

    getStatusLabel(status) {
      const labels = {
        pending: "قيد المراجعة",
        approved: "تمت الموافقة",
        rejected: "مرفوضة",
        cancelled: "ملغاة",
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case "pending":
          return "op-status-badge badge-pending";
        case "approved":
          return "op-status-badge badge-approved";
        case "rejected":
          return "op-status-badge badge-rejected";
        default:
          return "op-status-badge badge-default";
      }
    },

    viewDetails(item) {
      this.selectedRequest = item;
      this.detailsDialog = true;
    },

    openActionDialog(item, type) {
      this.selectedRequest = item;
      this.actionType = type;
      this.reviewNote = "";
      this.actionDialog = true;
    },

    submitAction() {
      if (!this.selectedRequest) return;

      if (this.actionType === "reject") {
        if (!this.reviewNote || this.reviewNote.trim().length < 3) {
          this.$notify({
            title: "تنبيه",
            text: "يرجى إدخال سبب الرفض (3 أحرف على الأقل)",
            type: "warning",
          });
          return;
        }
      }

      this.isSubmitting = true;
      const endpoint = `/driver-emergency-requests/${this.selectedRequest.id}/${this.actionType}`;

      axios
        .post(endpoint, {
          review_note: this.reviewNote ? this.reviewNote.trim() : null,
        })
        .then((response) => {
          this.$notify({
            title: "نجاح",
            text: response.data.message || "تم تحديث حالة الطلب بنجاح",
            type: "success",
          });

          this.actionDialog = false;
          this.loadRequests();
        })
        .catch((error) => {
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "حدث خطأ أثناء معالجة الطلب";

          this.$swal({
            title: "خطأ",
            text: msg,
            icon: "error",
            confirmButtonText: "موافق",
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
  },
};
</script>

<style scoped>
/* ==================== PAGE BASE ==================== */
.op-emergency-page {
  font-family: 'Cairo', sans-serif !important;
  color: #0F172A;
}

/* ==================== HEADER ==================== */
.op-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.op-main-title {
  font-size: 22px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  letter-spacing: -0.3px;
}

.op-sub-title {
  font-size: 13px;
  color: #64748B;
  font-weight: 500;
}

.op-icon-header-badge {
  background-color: #FEE2E2;
  border-radius: 10px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-pill-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
}

.bg-amber-soft {
  background-color: #FEF3C7;
}
.text-amber {
  color: #D97706 !important;
}

.bg-emerald-soft {
  background-color: #DCFCE7;
}
.text-emerald {
  color: #16A34A !important;
}

.text-navy {
  color: #082C69 !important;
}

.text-rose {
  color: #DC2626 !important;
}

.op-btn-refresh {
  background: #FFFFFF !important;
  color: #082C69 !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  padding: 0 16px !important;
  height: 38px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
  text-transform: none !important;
}
.op-btn-refresh:hover {
  background: #F8FAFC !important;
  border-color: #CBD5E1 !important;
}

/* ==================== 4 KPI CARDS GRID ==================== */
.op-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

@media (max-width: 960px) {
  .op-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 540px) {
  .op-kpi-grid {
    grid-template-columns: 1fr;
  }
}

.op-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(8, 44, 105, 0.06);
  border-color: #CBD5E1;
}

.op-kpi-card-active {
  border-color: #082C69 !important;
  background: #F8FAFC !important;
  box-shadow: 0 0 0 2px rgba(8, 44, 105, 0.12) !important;
}

.kpi-label {
  font-size: 12.5px;
  color: #64748B;
  font-weight: 600;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.kpi-icon-side {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bg-indigo-50 {
  background-color: #EEF2FF;
}
.bg-amber-50 {
  background-color: #FFFBEB;
}
.bg-emerald-50 {
  background-color: #F0FDF4;
}
.bg-rose-50 {
  background-color: #FEF2F2;
}

/* ==================== FILTER TOOLBAR ==================== */
.op-filter-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.op-search-box {
  max-width: 460px;
  min-width: 240px;
}

.op-select-box {
  min-width: 180px;
}

.op-search-input >>> .v-input__control,
.op-filter-select >>> .v-input__control {
  background-color: #F8FAFC !important;
  border-radius: 10px !important;
}

.op-search-input >>> fieldset,
.op-filter-select >>> fieldset {
  border-color: #E2E8F0 !important;
  border-width: 1px !important;
}

.op-search-input:hover >>> fieldset,
.op-filter-select:hover >>> fieldset {
  border-color: #082C69 !important;
}

.op-search-input >>> input {
  font-size: 13px !important;
  color: #0F172A !important;
}

.op-filter-select >>> .v-select__selections {
  font-size: 13px !important;
  color: #0F172A !important;
  font-weight: 600 !important;
}

/* ==================== TABLE CARD ==================== */
.op-table-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.op-data-table >>> table {
  border-collapse: collapse;
}

.op-data-table >>> thead th {
  background-color: #F8FAFC !important;
  color: #475569 !important;
  font-size: 12.5px !important;
  font-weight: 700 !important;
  border-bottom: 1px solid #E2E8F0 !important;
  padding: 12px 16px !important;
  white-space: nowrap;
}

.op-data-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.op-data-table >>> tbody tr:hover {
  background-color: #F8FAFC !important;
}

.op-data-table >>> tbody td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #F1F5F9 !important;
  vertical-align: middle;
}

/* ==================== PILLS & BADGES ==================== */
.op-code-pill {
  background-color: #F1F5F9;
  color: #082C69;
  font-size: 12px;
  font-family: monospace;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-block;
  border: 1px solid #E2E8F0;
}

.op-trip-pill {
  background-color: #EFF6FF;
  color: #1D4ED8;
  font-size: 12px;
  font-family: monospace;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-block;
  border: 1px solid #DBEAFE;
}

.op-reason-badge {
  background-color: #FFFBEB;
  color: #B45309;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #FDE68A;
  display: inline-flex;
  align-items: center;
}

.op-status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  line-height: 1;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.badge-pending {
  background-color: #FEF3C7;
  color: #B45309;
  border: 1px solid #FDE68A;
}
.badge-pending .status-dot {
  background-color: #D97706;
}

.badge-approved {
  background-color: #DCFCE7;
  color: #15803D;
  border: 1px solid #BBF7D0;
}
.badge-approved .status-dot {
  background-color: #16A34A;
}

.badge-rejected {
  background-color: #FEE2E2;
  color: #B91C1C;
  border: 1px solid #FECACA;
}
.badge-rejected .status-dot {
  background-color: #DC2626;
}

.badge-default {
  background-color: #F1F5F9;
  color: #64748B;
  border: 1px solid #E2E8F0;
}
.badge-default .status-dot {
  background-color: #94A3B8;
}

/* ==================== ACTION BUTTONS ==================== */
.op-action-btn {
  border-radius: 8px !important;
  background: #F8FAFC !important;
  transition: all 0.15s ease;
}
.op-action-btn:hover {
  background: #EEF2FF !important;
}

.op-action-approve:hover {
  background: #DCFCE7 !important;
}

.op-action-reject:hover {
  background: #FEE2E2 !important;
}

/* ==================== EMPTY STATE ==================== */
.op-empty-state {
  background: #FFFFFF;
}

.op-empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==================== MODALS ==================== */
.op-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  font-family: 'Cairo', sans-serif !important;
}

.op-dialog-header {
  background: #FAFCFE;
}

.op-dialog-title {
  font-size: 16px;
  font-weight: 800;
  color: #082C69;
}

.op-icon-header-box {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.op-trip-modal-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 600px) {
  .op-trip-modal-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.op-modal-stat-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.stat-label {
  font-size: 11px;
  color: #64748B;
  font-weight: 600;
}

.stat-value {
  font-size: 13.5px;
  font-weight: 700;
}

.op-btn-secondary {
  background: #FFFFFF !important;
  color: #64748B !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  font-weight: 700 !important;
  font-size: 13px !important;
}

.bg-slate-50 {
  background-color: #F8FAFC !important;
}

.text-xxs {
  font-size: 11px !important;
}
</style>
